"use client";

import React, { useEffect, useRef, useState } from "react";
import { cx } from "class-variance-authority";
import { useInView } from "framer-motion";
import Image from "next/image";
import { MovingCard } from "@/03-presenters/components/ui/moving-border";
import Slogen from "./Slogen";
import { useMediaQuery } from "usehooks-ts";
const DEFAULT_STYLE = {
  background: "#161419"
};
interface AnimatedTabProps {
  title: string;
  cardList: React.ReactNode[];
  descriptionList: {
    title: string;
    subTitle: string;
  }[];
  layout: "top-large" | "bottom-large";
  tilteBackground: string;
  borderBackground: string;
  containerClassNameList: string[];
  iconList: number[];
}
const GradientBorder: React.FC<{
  showBorder: boolean;
  children: React.ReactNode;
  className?: string;
  containerClassName: string;
  borderBackground: string;
  onClick?: () => void;
  borderClassName?: string;
}> = ({
  showBorder,
  children,
  className,
  onClick,
  containerClassName,
  borderClassName = "h-20 w-32",
  borderBackground
}) =>
// <div
//   className={cx("relative cursor-pointer bg-inherit p-[3px]", className)}
//   style={style}
//   onClick={onClick}
// >
//   <div
//     className={cx(
//       "absolute inset-0 rounded-[22px]",
//       showBorder ? "opacity-100" : "opacity-0",
//     )}
//     style={{ background: borderBackground }}
//   />
//   <div
//     className="relative z-10 rounded-[22px]"
//     style={showBorder ? DEFAULT_STYLE : undefined}
//   >
//     {children}
//   </div>
// </div>

<div className={cx("relative cursor-pointer bg-inherit", className)} onClick={onClick} data-sentry-component="GradientBorder" data-sentry-source-file="FourthBoxV2.tsx">
    <MovingCard isActive={showBorder} containerClassName={containerClassName} borderClassName={borderClassName} borderBackground={borderBackground} data-sentry-element="MovingCard" data-sentry-source-file="FourthBoxV2.tsx">
      {children}
    </MovingCard>
  </div>;
const GradientStaticBorder: React.FC<{
  showBorder: boolean;
  children: React.ReactNode;
  className?: string;
  borderBackground: string;
  onClick?: () => void;
}> = ({
  showBorder,
  children,
  className,
  onClick,
  borderBackground
}) => <div className={cx("relative cursor-pointer bg-inherit p-[2.5px]", className)} onClick={onClick} data-sentry-component="GradientStaticBorder" data-sentry-source-file="FourthBoxV2.tsx">
    <div className={cx("absolute inset-0 rounded-[22px]", showBorder ? "opacity-100" : "opacity-0")} style={{
    background: borderBackground
  }} />
    <div className="relative z-10 rounded-[22px]" style={showBorder ? DEFAULT_STYLE : undefined}>
      {children}
    </div>
  </div>;
const AnimatedTab: React.FC<AnimatedTabProps> = ({
  title,
  cardList,
  descriptionList,
  tilteBackground,
  layout,
  borderBackground,
  containerClassNameList,
  iconList
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true
  });
  const [isHover, setIsHover] = useState(false);
  useEffect(() => {
    if (isHover || !isInView) return;
    const interval = setInterval(() => setActiveIndex(prev => (prev + 1) % 3), 3000);
    return () => clearInterval(interval);
  }, [isHover, isInView]);
  const handleHover = (hover: boolean) => () => setIsHover(hover);
  const handleClick = (index: number) => () => setActiveIndex(index);
  const transformStyle = (direction: "left" | "right") => ({
    transform: !isInView ? `rotate(${direction === "left" ? "-" : ""}6deg) rotateX(6deg) rotateY(6deg) translateX(${direction === "left" ? "-" : ""}50px)` : undefined
  });
  const renderCards = () => {
    if (layout === "top-large") {
      return <>
          <GradientBorder borderBackground={borderBackground} showBorder={activeIndex === 0} onClick={handleClick(0)} className="mb-6" containerClassName={containerClassNameList[0]} borderClassName="h-20 w-48">
            <div onMouseEnter={handleHover(true)} onMouseLeave={handleHover(false)}>
              {cardList[0]}
            </div>
          </GradientBorder>
          <div className="flex justify-between">
            {[1, 2].map(index => <GradientBorder borderBackground={borderBackground} key={index} showBorder={activeIndex === index} onClick={handleClick(index)} className="w-[48%]" containerClassName={containerClassNameList[index]}>
                <div onMouseEnter={handleHover(true)} onMouseLeave={handleHover(false)}>
                  {cardList[index]}
                </div>
              </GradientBorder>)}
          </div>
        </>;
    } else {
      return <>
          <div className="mb-6 flex justify-between">
            {[0, 1].map(index => <GradientBorder borderBackground={borderBackground} key={index} showBorder={activeIndex === index} onClick={handleClick(index)} className="w-[48%]" containerClassName={containerClassNameList[index]}>
                <div onMouseEnter={handleHover(true)} onMouseLeave={handleHover(false)}>
                  {cardList[index]}
                </div>
              </GradientBorder>)}
          </div>
          <GradientBorder borderBackground={borderBackground} showBorder={activeIndex === 2} onClick={handleClick(2)} borderClassName="h-20 w-48" containerClassName={containerClassNameList[2]}>
            <div onMouseEnter={handleHover(true)} onMouseLeave={handleHover(false)}>
              {cardList[2]}
            </div>
          </GradientBorder>
        </>;
    }
  };
  return <div className="flex flex-col items-center justify-center pt-12" data-sentry-component="AnimatedTab" data-sentry-source-file="FourthBoxV2.tsx">
      <div className="flex w-fit items-center justify-center rounded-full px-6 py-1.5 text-[20px] font-semibold text-[#2C274B]" style={{
      background: tilteBackground
    }}>
        {title}
      </div>
      <div className="flex gap-20 py-20">
        <div className="flex flex-col transition-all duration-1000 ease-in-out" ref={ref} style={transformStyle("left")}>
          {renderCards()}
        </div>
        <div className="flex flex-1 flex-col justify-between transition-all duration-1000 ease-in-out" style={transformStyle("right")}>
          {descriptionList.map((description, index) => <GradientStaticBorder borderBackground={borderBackground} key={index} showBorder={activeIndex === index} onClick={handleClick(index)}>
              <div className="flex w-full flex-col items-start gap-4 p-10" onMouseEnter={handleHover(true)} onMouseLeave={handleHover(false)}>
                <div className="flex items-start justify-center gap-4">
                  <Image src={`/homepage/icon${iconList[index]}.webp`} alt="icon" width={index === 0 ? 36 : 30} height={index === 0 ? 36 : 30} />
                  <span className="text-2xl font-semibold text-white">
                    {description.title}
                  </span>
                </div>

                <span className="text-sm text-[rgba(255,255,255,0.6)]">
                  {description.subTitle}
                </span>
              </div>
            </GradientStaticBorder>)}
        </div>
      </div>
    </div>;
};
export function FourthBoxV2() {
  const gteSM = useMediaQuery("(min-width: 640px)", {
    defaultValue: true,
    initializeWithValue: false
  });
  if (!gteSM) return null;
  return <div data-sentry-component="FourthBoxV2" data-sentry-source-file="FourthBoxV2.tsx">
      <Slogen content={<div className="flex flex-col items-center gap-4 pt-20 text-white">
            <div className="text-[30px] font-semibold">
              深度拆解社媒和电商数据，提升营销效能
            </div>
            <div>AI 赋能全方位营销策略，数据助力科学决策</div>
          </div>} data-sentry-element="Slogen" data-sentry-source-file="FourthBoxV2.tsx" />
      <AnimatedTab title="发掘流量潜力爆款，解构爆品运营策略" tilteBackground="linear-gradient(90deg, #942BFA 0%, #BC4FF6 28.17%, #FF92BC 100%)" borderBackground="linear-gradient(90deg, #FD90BE 0%, #8E26EC 100%)" containerClassNameList={["w-[636px] h-[235px] rounded-3xl p-[3px]", "w-[306px] h-[235px] rounded-3xl p-[3px]", "w-[306px] h-[235px] rounded-3xl p-[3px]"]} cardList={[<Image key={1} src="/homepage/card1.webp" alt="card" style={{
      width: "630px",
      height: "229px",
      borderRadius: "1.75rem",
      objectFit: "cover"
    }} width={636} height={235} priority />, <Image key={2} src="/homepage/card2.webp" alt="card" style={{
      width: "300px",
      height: "229px",
      borderRadius: "1.75rem",
      objectFit: "cover"
    }} width={306} height={235} priority />, <Image key={3} src="/homepage/card3.webp" style={{
      width: "300px",
      height: "229px",
      borderRadius: "1.75rem",
      objectFit: "cover"
    }} alt="card" width={306} height={235} priority />]} iconList={[1, 2, 3]} descriptionList={[{
      title: "爆品趋势发现",
      subTitle: "跟踪爆品雷达，抢占市场先机"
    }, {
      title: "视频文案生成",
      subTitle: "一键为商品生成带货视频脚本"
    }, {
      title: "投放效果分析",
      subTitle: "洞察投流效果，提升投放效率"
    }]} layout="top-large" data-sentry-element="AnimatedTab" data-sentry-source-file="FourthBoxV2.tsx" />
      <AnimatedTab title="海量商品评论分析，理解消费者需求，洞察潜在商机" tilteBackground="linear-gradient(90deg, #3082F2 0%, #8AB8FA 51.23%, #98E3FD 100%)" borderBackground="linear-gradient(90deg, #8CDDFF 0%, #348AFF 100%)" cardList={[<Image key={1} src="/homepage/card4.webp" alt="card" width={306} height={235} style={{
      width: "300px",
      height: "229px",
      borderRadius: "1.75rem",
      objectFit: "cover"
    }} priority />, <Image key={2} src="/homepage/card5.webp" alt="card" width={306} height={235} style={{
      width: "300px",
      height: "229px",
      borderRadius: "1.75rem",
      objectFit: "cover"
    }} priority />, <Image key={3} src="/homepage/card6.webp" alt="card" width={636} height={235} style={{
      width: "630px",
      height: "229px",
      borderRadius: "1.75rem",
      objectFit: "cover"
    }} priority />]} iconList={[4, 5, 6]} containerClassNameList={["w-[306px] h-[235px] rounded-3xl p-[3px]", "w-[306px] h-[235px] rounded-3xl p-[3px]", "w-[636px] h-[235px] rounded-3xl p-[3px]"]} descriptionList={[{
      title: "海量评论采集",
      subTitle: "倾听用户反馈，挖掘需求本质"
    }, {
      title: "多维度属性评价",
      subTitle: "多维度商品属性分析，打造产品差异化"
    }, {
      title: "视频脚本仿写",
      subTitle: "视频脚本仿写支持爆款视频脚本导出，并根据商品卖点一键仿写"
    }]} layout="bottom-large" data-sentry-element="AnimatedTab" data-sentry-source-file="FourthBoxV2.tsx" />
      <AnimatedTab title="实时掌握直播动态，强占流量风口，提升直播转化效率" tilteBackground="linear-gradient(90deg, #2394AF 0%, #7CE0D1 51.23%, #94F4E1 100%)" borderBackground="linear-gradient(90deg, #92F3E0 0%, #2C9CB3 100%)" cardList={[<Image key={1} src="/homepage/card10.webp" alt="card" style={{
      width: "630px",
      height: "229px",
      borderRadius: "1.75rem",
      objectFit: "cover"
    }} width={636} height={235} priority />, <Image key={2} src="/homepage/card11.webp" alt="card" style={{
      width: "300px",
      height: "229px",
      borderRadius: "1.75rem",
      objectFit: "cover"
    }} width={306} height={235} priority />, <Image key={3} src="/homepage/card12.webp" style={{
      width: "300px",
      height: "229px",
      borderRadius: "1.75rem",
      objectFit: "cover"
    }} alt="card" width={306} height={235} priority />]} iconList={[10, 11, 12]} containerClassNameList={["w-[636px] h-[235px] rounded-3xl p-[3px]", "w-[306px] h-[235px] rounded-3xl p-[3px]", "w-[306px] h-[235px] rounded-3xl p-[3px]"]} descriptionList={[{
      title: "爆款直播间发现",
      subTitle: "开播即刻监控，先人一步发现爆款直播"
    }, {
      title: "实时成交分析",
      subTitle: "成交数据实时更新，动态跟踪直播效果"
    }, {
      title: "直播录屏回看",
      subTitle: "回看直播，细节复盘，让成功可复制"
    }]} layout="top-large" data-sentry-element="AnimatedTab" data-sentry-source-file="FourthBoxV2.tsx" />
      <AnimatedTab title="全面掌握行业发展态势，助力优化竞对策略" tilteBackground="linear-gradient(90deg, #22A8B5 0%, #A0E999 51.23%, #E5F99D 100%)" borderBackground="linear-gradient(90deg, #DBF79D 0%, #34B2B1 100%)" containerClassNameList={["w-[636px] h-[235px] rounded-3xl p-[3px]", "w-[306px] h-[235px] rounded-3xl p-[3px]", "w-[306px] h-[235px] rounded-3xl p-[3px]"]} cardList={[<Image key={1} src="/homepage/card7.webp" alt="card" style={{
      width: "630px",
      height: "229px",
      borderRadius: "1.75rem",
      objectFit: "cover"
    }} width={636} height={235} priority />, <Image key={2} src="/homepage/card8.webp" alt="card" style={{
      width: "300px",
      height: "229px",
      borderRadius: "1.75rem",
      objectFit: "cover"
    }} width={306} height={235} priority />, <Image key={3} src="/homepage/card9.webp" alt="card" style={{
      width: "300px",
      height: "229px",
      borderRadius: "1.75rem",
      objectFit: "cover"
    }} width={306} height={235} priority />]} iconList={[7, 8, 9]} descriptionList={[{
      title: "品类数据全景图展示",
      subTitle: "追踪品类发展动向，捕捉行业前沿趋势"
    }, {
      title: "企业信息检索",
      subTitle: "发掘企业信息，了解行业地域分布"
    }, {
      title: "店铺运营策略洞察",
      subTitle: "店铺运营策略一览无余，助力优化竞对策略"
    }]} layout="top-large" data-sentry-element="AnimatedTab" data-sentry-source-file="FourthBoxV2.tsx" />
    </div>;
}